import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Check,
  FileDown,
  Pencil,
  Plus,
  Search,
  Trash2,
  X,
} from 'lucide-react';
import { useUIColors } from '../../../../hooks/useTheme';
import { useAuth } from '../../../../contexts/AuthContext';
import {
  approveExpenseReport,
  createExpenseReport,
  deleteExpenseReport,
  getExpenseReport,
  getExpenseReportItems,
  getExpenseReports,
  rejectExpenseReport,
  updateExpenseReport,
} from '../../../../services/treasuryAPI';
import { downloadRendicionPdf } from '../../../../utils/rendicionPdf';
import { isDateInRange, isDateInSelectedPeriod, parseDateSafe } from '../../../placeholders/periodSummaryUtils';

const REPORT_TYPES = ['Rendición de gastos', 'Fondo por rendir', 'Viático'];
const ITEM_CATEGORIES = [
  'Transporte',
  'Alimentación',
  'Alojamiento',
  'Combustible',
  'Materiales',
  'Servicios',
  'Otro',
];
const ITEM_PAYMENT_METHODS = ['Efectivo', 'Transferencia', 'Tarjeta', 'Cheque', 'Otro'];
const ITEM_TITULARES = ['Personal', 'Soquimat'];
const ITEM_CURRENCIES = ['CLP', 'USD', 'EUR', 'UF'];

const TABS = [
  { key: 'aprobaciones', label: 'Aprobaciones' },
  { key: 'historial', label: 'Historial Rendiciones' },
  { key: 'items', label: 'Historial Items' },
];

const money = (value) =>
  new Intl.NumberFormat('es-CL', {
    style: 'currency',
    currency: 'CLP',
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(Number(value || 0));

const itemAmountClp = (item) => {
  const amount = Number(item.amount || 0);
  const currency = String(item.currency || 'CLP').trim().toUpperCase() || 'CLP';
  if (currency === 'CLP') return amount;
  const rate = Number(item.exchange_rate || 0);
  return amount * (rate > 0 ? rate : 1);
};

const emptyItem = () => ({
  item_date: new Date().toISOString().slice(0, 10),
  category: 'Otro',
  description: '',
  document_number: '',
  issuer_rut: '',
  issuer_name: '',
  payment_method: 'Efectivo',
  titular: 'Personal',
  currency: 'CLP',
  exchange_rate: '1',
  amount: '',
  notes: '',
});

const requesterFromUser = (user) => {
  const collaborator = user?.collaborator || null;
  return {
    requester_rut: String(collaborator?.rut || user?.rut || '').trim(),
    requester_name: String(collaborator?.full_name || user?.full_name || '').trim(),
  };
};

const emptyForm = (user = null) => ({
  report_date: new Date().toISOString().slice(0, 10),
  report_type: 'Rendición de gastos',
  ...requesterFromUser(user),
  description: '',
  notes: '',
  items: [emptyItem()],
});

const statusPresentation = (statusRaw, isDarkMode) => {
  const s = String(statusRaw || '')
    .trim()
    .toLowerCase();
  const label = statusRaw && String(statusRaw).trim() ? String(statusRaw).trim() : '—';
  if (s === 'aprobada') {
    return {
      label,
      bg: isDarkMode ? '#14532d' : '#dcfce7',
      color: isDarkMode ? '#bbf7d0' : '#166534',
      border: isDarkMode ? '#22c55e' : '#86efac',
    };
  }
  if (s === 'rechazada' || s === 'anulada') {
    return {
      label,
      bg: isDarkMode ? '#3f3f46' : '#f4f4f5',
      color: isDarkMode ? '#d4d4d8' : '#52525b',
      border: isDarkMode ? '#52525b' : '#d4d4d8',
    };
  }
  return {
    label,
    bg: isDarkMode ? '#1e3a5f' : '#e0f2fe',
    color: isDarkMode ? '#7dd3fc' : '#0369a1',
    border: isDarkMode ? '#0284c7' : '#7dd3fc',
  };
};

const StatusChip = ({ status, isDarkMode }) => {
  const tone = statusPresentation(status, isDarkMode);
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        padding: '2px 8px',
        borderRadius: 999,
        fontSize: 11,
        fontWeight: 700,
        background: tone.bg,
        color: tone.color,
        border: `1px solid ${tone.border}`,
        whiteSpace: 'nowrap',
      }}
    >
      {tone.label}
    </span>
  );
};

/**
 * Contabilidad · Rendiciones: Aprobaciones / Historial / Items + formulario.
 */
const AccountingRendicionesPanel = ({
  ui: uiProp,
  isDarkMode = false,
  onBooksChanged,
  dateFrom = '',
  dateTo = '',
  selectedPeriod = null,
}) => {
  const themeUi = useUIColors();
  const ui = uiProp || themeUi;
  const { user } = useAuth();
  const border = ui.border;
  const text = ui.text;
  const muted = ui.muted;
  const bg = ui.page || ui.panel;
  const headerBg = ui.header || ui.panel;
  const inputBg = ui.inputBg || ui.panel || bg;
  const inputBorder = ui.inputBorder || border;

  const [tab, setTab] = useState('aprobaciones');
  const [reports, setReports] = useState([]);
  const [items, setItems] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(() => emptyForm());
  const [formError, setFormError] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [actionBusyId, setActionBusyId] = useState(null);

  const inputStyle = {
    height: 34,
    borderRadius: 8,
    border: `1px solid ${inputBorder}`,
    background: inputBg,
    color: ui.inputText || text,
    padding: '0 10px',
    fontSize: 13,
    width: '100%',
    boxSizing: 'border-box',
  };
  const primaryBtn = {
    height: 34,
    borderRadius: 8,
    border: 'none',
    background: ui.interactive || '#16a34a',
    color: ui.textOnAccent || '#fff',
    padding: '0 12px',
    cursor: 'pointer',
    fontSize: 13,
    fontWeight: 600,
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
  };
  const ghostBtn = {
    height: 28,
    borderRadius: 8,
    border: `1px solid ${border}`,
    background: 'transparent',
    color: text,
    padding: '0 8px',
    cursor: 'pointer',
    fontSize: 12,
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
  };

  const load = useCallback(async () => {
    try {
      setIsLoading(true);
      setError('');
      const [reportRows, itemRows] = await Promise.all([
        getExpenseReports(),
        getExpenseReportItems(),
      ]);
      setReports(Array.isArray(reportRows) ? reportRows : []);
      setItems(Array.isArray(itemRows) ? itemRows : []);
    } catch (err) {
      setError(err.message || 'No se pudieron cargar las rendiciones.');
      setReports([]);
      setItems([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const periodRange = useMemo(() => {
    if (!dateFrom && !dateTo) return null;
    const start = dateFrom ? parseDateSafe(dateFrom) : null;
    const end = dateTo ? parseDateSafe(dateTo) : null;
    if (!start && !end) return null;
    return {
      start: start || new Date(0),
      end: end || new Date(8640000000000000),
    };
  }, [dateFrom, dateTo]);

  const inPeriod = useCallback(
    (dateValue) => {
      if (selectedPeriod) return isDateInSelectedPeriod(dateValue, selectedPeriod);
      if (!periodRange) return true;
      return isDateInRange(dateValue, periodRange);
    },
    [selectedPeriod, periodRange]
  );

  const tabCounts = useMemo(() => {
    const pending = reports.filter((r) => r.status === 'Pendiente' && inPeriod(r.report_date)).length;
    const all = reports.filter((r) => inPeriod(r.report_date)).length;
    const itemCount = items.filter((i) => inPeriod(i.item_date || i.report_date)).length;
    return { aprobaciones: pending, historial: all, items: itemCount };
  }, [reports, items, inPeriod]);

  const filteredReports = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    let rows = reports.filter((r) => inPeriod(r.report_date));
    if (tab === 'aprobaciones') {
      rows = rows.filter((r) => r.status === 'Pendiente');
    }
    if (!q) return rows;
    return rows.filter((row) =>
      [
        row.report_code,
        row.report_type,
        row.requester_name,
        row.requester_rut,
        row.description,
        row.status,
        row.notes,
      ]
        .join(' ')
        .toLowerCase()
        .includes(q)
    );
  }, [reports, tab, searchQuery, inPeriod]);

  const filteredItems = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    let rows = items.filter((i) => inPeriod(i.item_date || i.report_date));
    if (!q) return rows;
    return rows.filter((row) =>
      [
        row.report_code,
        row.category,
        row.description,
        row.document_number,
        row.issuer_name,
        row.issuer_rut,
        row.payment_method,
        row.titular,
        row.currency,
        row.requester_name,
        row.report_type,
        row.report_status,
      ]
        .join(' ')
        .toLowerCase()
        .includes(q)
    );
  }, [items, searchQuery, inPeriod]);

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm(user));
    setFormError('');
    setShowForm(true);
  };

  const openEdit = async (row) => {
    try {
      setFormError('');
      const full = await getExpenseReport(row.id);
      setEditingId(full.id);
      setForm({
        report_date: String(full.report_date || '').slice(0, 10),
        report_type: full.report_type || 'Rendición de gastos',
        requester_rut: full.requester_rut || requesterFromUser(user).requester_rut,
        requester_name: full.requester_name || requesterFromUser(user).requester_name,
        description: full.description || '',
        notes: full.notes || '',
        items:
          Array.isArray(full.items) && full.items.length
            ? full.items.map((item) => ({
                item_date: String(item.item_date || full.report_date || '').slice(0, 10),
                category: item.category || 'Otro',
                description: item.description || '',
                document_number: item.document_number || '',
                issuer_rut: item.issuer_rut || '',
                issuer_name: item.issuer_name || '',
                payment_method: item.payment_method || 'Efectivo',
                titular: item.titular === 'Soquimat' ? 'Soquimat' : 'Personal',
                currency: String(item.currency || 'CLP').toUpperCase() || 'CLP',
                exchange_rate: String(item.exchange_rate ?? 1),
                amount: String(item.amount ?? ''),
                notes: item.notes || '',
              }))
            : [emptyItem()],
      });
      setShowForm(true);
    } catch (err) {
      window.alert(err.message || 'No se pudo abrir la rendición.');
    }
  };

  const formTotal = useMemo(
    () => form.items.reduce((acc, item) => acc + itemAmountClp(item), 0),
    [form.items]
  );

  const formTotalToReturn = useMemo(
    () =>
      form.items.reduce((acc, item) => {
        if (String(item.titular || '').trim() === 'Soquimat') return acc;
        return acc + itemAmountClp(item);
      }, 0),
    [form.items]
  );

  const updateItem = (index, patch) => {
    setForm((prev) => ({
      ...prev,
      items: prev.items.map((item, i) => {
        if (i !== index) return item;
        const next = { ...item, ...patch };
        if (Object.prototype.hasOwnProperty.call(patch, 'currency')) {
          const currency = String(patch.currency || 'CLP').toUpperCase();
          next.currency = currency;
          if (currency === 'CLP') next.exchange_rate = '1';
        }
        return next;
      }),
    }));
  };

  const addItem = () => {
    setForm((prev) => ({ ...prev, items: [...prev.items, emptyItem()] }));
  };

  const removeItem = (index) => {
    setForm((prev) => ({
      ...prev,
      items: prev.items.length <= 1 ? prev.items : prev.items.filter((_, i) => i !== index),
    }));
  };

  const handleSave = async () => {
    if (!form.report_date) {
      setFormError('La fecha es obligatoria.');
      return;
    }
    const fromUser = requesterFromUser(user);
    const requesterName = form.requester_name || fromUser.requester_name;
    const requesterRut = form.requester_rut || fromUser.requester_rut;
    if (!String(requesterName || '').trim()) {
      setFormError('No se pudo identificar el solicitante. Vuelve a iniciar sesión.');
      return;
    }
    const payloadItems = form.items
      .map((item, index) => ({
        item_date: item.item_date || form.report_date,
        category: item.category || 'Otro',
        description: item.description || '',
        document_number: item.document_number || '',
        issuer_rut: item.issuer_rut || '',
        issuer_name: item.issuer_name || '',
        payment_method: item.payment_method || '',
        titular: item.titular === 'Soquimat' ? 'Soquimat' : 'Personal',
        currency: String(item.currency || 'CLP').toUpperCase() || 'CLP',
        exchange_rate:
          String(item.currency || 'CLP').toUpperCase() === 'CLP'
            ? 1
            : Number(item.exchange_rate || 1),
        amount: Number(item.amount || 0),
        notes: item.notes || '',
        line_order: (index + 1) * 10,
      }))
      .filter((item) => item.amount > 0);
    if (!payloadItems.length) {
      setFormError('Agrega al menos un ítem con monto mayor a 0.');
      return;
    }
    const payload = {
      report_date: form.report_date,
      report_type: form.report_type,
      requester_rut: requesterRut,
      requester_name: requesterName,
      requester_user_id: user?.id || null,
      description: form.description,
      notes: form.notes,
      status: 'Pendiente',
      items: payloadItems,
    };
    try {
      setIsSaving(true);
      setFormError('');
      if (editingId) await updateExpenseReport(editingId, payload);
      else await createExpenseReport(payload);
      setShowForm(false);
      await load();
      onBooksChanged?.();
      if (!editingId) setTab('aprobaciones');
    } catch (err) {
      setFormError(err.message || 'No se pudo guardar.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDownloadPdf = async (row) => {
    try {
      setActionBusyId(row.id);
      const full = await getExpenseReport(row.id);
      await downloadRendicionPdf(full);
    } catch (err) {
      window.alert(err.message || 'No se pudo generar el PDF.');
    } finally {
      setActionBusyId(null);
    }
  };

  const handleApprove = async (row) => {
    if (!window.confirm(`¿Aprobar la rendición ${row.report_code}?`)) return;
    try {
      setActionBusyId(row.id);
      await approveExpenseReport(row.id);
      await load();
      onBooksChanged?.();
    } catch (err) {
      window.alert(err.message || 'No se pudo aprobar.');
    } finally {
      setActionBusyId(null);
    }
  };

  const handleReject = async (row) => {
    const reason = window.prompt(`Motivo del rechazo de ${row.report_code}:`, '') ?? null;
    if (reason === null) return;
    try {
      setActionBusyId(row.id);
      await rejectExpenseReport(row.id, reason);
      await load();
      onBooksChanged?.();
    } catch (err) {
      window.alert(err.message || 'No se pudo rechazar.');
    } finally {
      setActionBusyId(null);
    }
  };

  const handleDelete = async (row) => {
    if (!window.confirm(`¿Anular la rendición ${row.report_code}?`)) return;
    try {
      setActionBusyId(row.id);
      await deleteExpenseReport(row.id);
      await load();
      onBooksChanged?.();
    } catch (err) {
      window.alert(err.message || 'No se pudo anular.');
    } finally {
      setActionBusyId(null);
    }
  };

  const subtitle =
    tab === 'items'
      ? loadingLabel(isLoading, error, filteredItems.length, 'ítem', 'ítems', filteredItems)
      : loadingLabel(isLoading, error, filteredReports.length, 'rendición', 'rendiciones', filteredReports);

  return (
    <div style={{ display: 'grid', gap: 12 }}>
      <div
        style={{
          border: `1px solid ${border}`,
          borderRadius: 12,
          overflow: 'hidden',
          background: bg,
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '12px 16px',
            borderBottom: `1px solid ${border}`,
            background: headerBg,
            flexWrap: 'wrap',
            gap: 10,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, flexWrap: 'wrap', minWidth: 0 }}>
            <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: text }}>Rendiciones</h3>
            <p style={{ margin: 0, fontSize: 12, color: muted }}>{subtitle}</p>
          </div>
          <button type="button" onClick={openCreate} style={{ ...primaryBtn, height: 36, padding: '0 14px' }}>
            <Plus size={16} /> Nueva rendición
          </button>
        </div>

        <div
          role="tablist"
          aria-label="Vistas de rendiciones"
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 4,
            padding: '0 12px',
            borderBottom: `1px solid ${border}`,
            background: headerBg,
            overflowX: 'auto',
          }}
        >
          {TABS.map((item) => {
            const active = tab === item.key;
            const count = tabCounts[item.key] || 0;
            return (
              <button
                key={item.key}
                type="button"
                role="tab"
                aria-selected={active}
                onClick={() => {
                  setTab(item.key);
                  setSearchQuery('');
                }}
                style={{
                  appearance: 'none',
                  border: 'none',
                  background: 'transparent',
                  cursor: 'pointer',
                  padding: '10px 12px',
                  marginBottom: -1,
                  fontSize: 13,
                  fontWeight: active ? 700 : 500,
                  color: active ? text : muted,
                  borderBottom: active ? '2px solid #16a34a' : '2px solid transparent',
                  whiteSpace: 'nowrap',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                }}
              >
                <span>{item.label}</span>
                <span
                  style={{
                    fontSize: 11,
                    fontWeight: 600,
                    color: active ? (isDarkMode ? '#86efac' : '#15803d') : muted,
                    background: active
                      ? isDarkMode
                        ? 'rgba(22, 163, 74, 0.2)'
                        : 'rgba(22, 163, 74, 0.12)'
                      : isDarkMode
                        ? 'rgba(255,255,255,0.06)'
                        : 'rgba(0,0,0,0.05)',
                    borderRadius: 999,
                    padding: '1px 7px',
                    minWidth: 18,
                    textAlign: 'center',
                  }}
                >
                  {count}
                </span>
              </button>
            );
          })}
        </div>

        <div
          style={{
            padding: '10px 16px',
            borderBottom: `1px solid ${border}`,
            display: 'flex',
            flexWrap: 'wrap',
            gap: 10,
            alignItems: 'flex-end',
          }}
        >
          <label style={{ display: 'flex', flexDirection: 'column', gap: 6, flex: '1 1 220px' }}>
            <span style={{ fontSize: 11, fontWeight: 600, color: muted, letterSpacing: '0.04em' }}>
              Buscar
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Search size={16} color={muted} aria-hidden />
              <input
                type="search"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={
                  tab === 'items'
                    ? 'Código, categoría, descripción…'
                    : 'Código, solicitante, tipo…'
                }
                style={{ ...inputStyle, flex: 1, minWidth: 0 }}
              />
            </span>
          </label>
        </div>

        <div style={{ overflowX: 'auto' }}>
          {tab === 'items' ? (
            <ItemsTable
              rows={filteredItems}
              isLoading={isLoading}
              error={error}
              isDarkMode={isDarkMode}
              ui={ui}
            />
          ) : (
            <ReportsTable
              rows={filteredReports}
              isLoading={isLoading}
              error={error}
              isDarkMode={isDarkMode}
              ui={ui}
              showApprovals={tab === 'aprobaciones'}
              actionBusyId={actionBusyId}
              onEdit={openEdit}
              onDownloadPdf={handleDownloadPdf}
              onApprove={handleApprove}
              onReject={handleReject}
              onDelete={handleDelete}
            />
          )}
        </div>
      </div>

      {showForm ? (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: ui.overlay || 'rgba(0,0,0,0.45)',
            zIndex: 10000,
            display: 'flex',
            justifyContent: 'flex-end',
          }}
        >
          <div
            style={{
              width: 560,
              maxWidth: '100%',
              height: '100%',
              background: ui.panel,
              borderLeft: `1px solid ${border}`,
              padding: 20,
              boxSizing: 'border-box',
              display: 'grid',
              gridTemplateRows: 'auto 1fr auto',
              gap: 16,
            }}
          >
            <div>
              <p style={{ margin: 0, fontSize: 16, fontWeight: 700, color: text }}>
                {editingId ? 'Editar rendición' : 'Nueva rendición'}
              </p>
              <p style={{ margin: '4px 0 0', fontSize: 12, color: muted }}>
                Completa la cabecera y agrega los ítems de gasto.
              </p>
            </div>

            <div style={{ overflowY: 'auto', display: 'grid', gap: 12, alignContent: 'start' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <label style={{ display: 'grid', gap: 6, fontSize: 12, color: ui.textSecondary || muted }}>
                  Fecha
                  <input
                    type="date"
                    value={form.report_date}
                    onChange={(e) => setForm((p) => ({ ...p, report_date: e.target.value }))}
                    style={inputStyle}
                  />
                </label>
                <label style={{ display: 'grid', gap: 6, fontSize: 12, color: ui.textSecondary || muted }}>
                  Tipo
                  <select
                    value={form.report_type}
                    onChange={(e) => setForm((p) => ({ ...p, report_type: e.target.value }))}
                    style={inputStyle}
                  >
                    {REPORT_TYPES.map((type) => (
                      <option key={type} value={type}>
                        {type}
                      </option>
                    ))}
                  </select>
                </label>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '140px 1fr', gap: 10 }}>
                <label style={{ display: 'grid', gap: 6, fontSize: 12, color: ui.textSecondary || muted }}>
                  RUT solicitante
                  <input
                    value={form.requester_rut}
                    readOnly
                    style={{
                      ...inputStyle,
                      opacity: 0.85,
                      cursor: 'default',
                      background: isDarkMode ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)',
                    }}
                    placeholder="Sin RUT en perfil"
                    title="Se carga desde el usuario autenticado"
                  />
                </label>
                <label style={{ display: 'grid', gap: 6, fontSize: 12, color: ui.textSecondary || muted }}>
                  Solicitante
                  <input
                    value={form.requester_name}
                    readOnly
                    style={{
                      ...inputStyle,
                      opacity: 0.85,
                      cursor: 'default',
                      background: isDarkMode ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)',
                    }}
                    placeholder="Usuario autenticado"
                    title="Se carga desde el usuario autenticado"
                  />
                </label>
              </div>
              <p style={{ margin: 0, fontSize: 11, color: muted }}>
                El solicitante se completa con el colaborador vinculado a tu usuario
                {!form.requester_rut
                  ? '. Si falta el RUT, complétalo en Recursos Humanos → Equipo.'
                  : '.'}
              </p>

              <label style={{ display: 'grid', gap: 6, fontSize: 12, color: ui.textSecondary || muted }}>
                Descripción
                <input
                  value={form.description}
                  onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
                  style={inputStyle}
                  placeholder="Motivo o referencia"
                />
              </label>

              <label style={{ display: 'grid', gap: 6, fontSize: 12, color: ui.textSecondary || muted }}>
                Notas
                <textarea
                  value={form.notes}
                  onChange={(e) => setForm((p) => ({ ...p, notes: e.target.value }))}
                  rows={2}
                  style={{
                    ...inputStyle,
                    height: 'auto',
                    padding: '8px 10px',
                    resize: 'vertical',
                    fontFamily: 'inherit',
                  }}
                />
              </label>

              <div
                style={{
                  border: `1px solid ${border}`,
                  borderRadius: 10,
                  padding: 12,
                  display: 'grid',
                  gap: 10,
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <p style={{ margin: 0, fontSize: 13, fontWeight: 700, color: text }}>Ítems de gasto</p>
                  <button type="button" onClick={addItem} style={ghostBtn}>
                    <Plus size={14} /> Agregar ítem
                  </button>
                </div>

                {form.items.map((item, index) => (
                  <div
                    key={`item-${index}`}
                    style={{
                      display: 'grid',
                      gap: 8,
                      padding: 10,
                      borderRadius: 8,
                      background: isDarkMode ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
                      border: `1px solid ${border}`,
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontSize: 12, fontWeight: 600, color: muted }}>Ítem {index + 1}</span>
                      <button
                        type="button"
                        onClick={() => removeItem(index)}
                        disabled={form.items.length <= 1}
                        style={{
                          ...ghostBtn,
                          opacity: form.items.length <= 1 ? 0.4 : 1,
                          cursor: form.items.length <= 1 ? 'not-allowed' : 'pointer',
                        }}
                      >
                        <Trash2 size={13} />
                      </button>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                      <label style={{ display: 'grid', gap: 4, fontSize: 11, color: muted }}>
                        Fecha
                        <input
                          type="date"
                          value={item.item_date}
                          onChange={(e) => updateItem(index, { item_date: e.target.value })}
                          style={inputStyle}
                        />
                      </label>
                      <label style={{ display: 'grid', gap: 4, fontSize: 11, color: muted }}>
                        Categoría
                        <select
                          value={item.category}
                          onChange={(e) => updateItem(index, { category: e.target.value })}
                          style={inputStyle}
                        >
                          {ITEM_CATEGORIES.map((cat) => (
                            <option key={cat} value={cat}>
                              {cat}
                            </option>
                          ))}
                        </select>
                      </label>
                    </div>
                    <label style={{ display: 'grid', gap: 4, fontSize: 11, color: muted }}>
                      Descripción
                      <input
                        value={item.description}
                        onChange={(e) => updateItem(index, { description: e.target.value })}
                        style={inputStyle}
                        placeholder="Detalle del gasto"
                      />
                    </label>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                      <label style={{ display: 'grid', gap: 4, fontSize: 11, color: muted }}>
                        Nº documento
                        <input
                          value={item.document_number}
                          onChange={(e) => updateItem(index, { document_number: e.target.value })}
                          style={inputStyle}
                          placeholder="Boleta / factura"
                        />
                      </label>
                      <label style={{ display: 'grid', gap: 4, fontSize: 11, color: muted }}>
                        Monto
                        <input
                          type="number"
                          value={item.amount}
                          onChange={(e) => updateItem(index, { amount: e.target.value })}
                          style={inputStyle}
                          min="0"
                          step="0.01"
                        />
                      </label>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                      <label style={{ display: 'grid', gap: 4, fontSize: 11, color: muted }}>
                        Moneda
                        <select
                          value={item.currency || 'CLP'}
                          onChange={(e) => updateItem(index, { currency: e.target.value })}
                          style={inputStyle}
                        >
                          {ITEM_CURRENCIES.map((currency) => (
                            <option key={currency} value={currency}>
                              {currency}
                            </option>
                          ))}
                        </select>
                      </label>
                      <label style={{ display: 'grid', gap: 4, fontSize: 11, color: muted }}>
                        Cambio a CLP
                        <input
                          type="number"
                          value={item.currency === 'CLP' ? '1' : item.exchange_rate}
                          onChange={(e) => updateItem(index, { exchange_rate: e.target.value })}
                          style={inputStyle}
                          min="0"
                          step="0.0001"
                          disabled={String(item.currency || 'CLP').toUpperCase() === 'CLP'}
                          title="Tipo de cambio para convertir el monto a CLP"
                        />
                      </label>
                    </div>
                    {String(item.currency || 'CLP').toUpperCase() !== 'CLP' && Number(item.amount || 0) > 0 ? (
                      <div style={{ fontSize: 11, color: muted, textAlign: 'right' }}>
                        Equivalente: {money(itemAmountClp(item))}
                      </div>
                    ) : null}
                    <div style={{ display: 'grid', gridTemplateColumns: '140px 1fr', gap: 8 }}>
                      <label style={{ display: 'grid', gap: 4, fontSize: 11, color: muted }}>
                        RUT emisor
                        <input
                          value={item.issuer_rut}
                          onChange={(e) => updateItem(index, { issuer_rut: e.target.value })}
                          style={inputStyle}
                          placeholder="12.345.678-9"
                        />
                      </label>
                      <label style={{ display: 'grid', gap: 4, fontSize: 11, color: muted }}>
                        Nombre emisor
                        <input
                          value={item.issuer_name}
                          onChange={(e) => updateItem(index, { issuer_name: e.target.value })}
                          style={inputStyle}
                          placeholder="Quien emite la boleta o factura"
                        />
                      </label>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                      <label style={{ display: 'grid', gap: 4, fontSize: 11, color: muted }}>
                        Método de pago
                        <select
                          value={item.payment_method || 'Efectivo'}
                          onChange={(e) => updateItem(index, { payment_method: e.target.value })}
                          style={inputStyle}
                        >
                          {ITEM_PAYMENT_METHODS.map((method) => (
                            <option key={method} value={method}>
                              {method}
                            </option>
                          ))}
                        </select>
                      </label>
                      <label style={{ display: 'grid', gap: 4, fontSize: 11, color: muted }}>
                        Titular
                        <select
                          value={item.titular || 'Personal'}
                          onChange={(e) => updateItem(index, { titular: e.target.value })}
                          style={inputStyle}
                          title="Soquimat suma al total pero no al total a devolver"
                        >
                          {ITEM_TITULARES.map((titular) => (
                            <option key={titular} value={titular}>
                              {titular}
                            </option>
                          ))}
                        </select>
                      </label>
                    </div>
                  </div>
                ))}

                <div
                  style={{
                    display: 'grid',
                    gap: 4,
                    justifyItems: 'end',
                    fontSize: 13,
                    color: text,
                  }}
                >
                  <div style={{ fontWeight: 700 }}>Total rendición (CLP): {money(formTotal)}</div>
                  <div style={{ fontWeight: 600, color: muted }}>
                    Total a devolver (CLP): {money(formTotalToReturn)}
                  </div>
                </div>
              </div>

              {formError ? (
                <p style={{ margin: 0, fontSize: 12, color: '#dc2626' }}>{formError}</p>
              ) : null}
            </div>

            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button type="button" onClick={() => setShowForm(false)} style={{ ...ghostBtn, height: 34 }}>
                Cancelar
              </button>
              <button type="button" onClick={handleSave} disabled={isSaving} style={primaryBtn}>
                {isSaving ? 'Guardando…' : editingId ? 'Guardar cambios' : 'Crear rendición'}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

function loadingLabel(isLoading, error, count, singular, plural, rowsForTotal) {
  if (isLoading) return 'Cargando datos...';
  if (error) return error;
  const total = (rowsForTotal || []).reduce((acc, row) => acc + Number(row.total_amount || row.amount || 0), 0);
  return `${count} ${count === 1 ? singular : plural} · ${money(total)}`;
}

function ReportsTable({
  rows,
  isLoading,
  error,
  isDarkMode,
  ui,
  showApprovals,
  actionBusyId,
  onEdit,
  onDownloadPdf,
  onApprove,
  onReject,
  onDelete,
}) {
  const th = {
    textAlign: 'left',
    padding: '8px 10px',
    fontSize: 11,
    fontWeight: 700,
    color: ui.muted,
    borderBottom: `1px solid ${ui.border}`,
    whiteSpace: 'nowrap',
  };
  const td = {
    padding: '9px 10px',
    fontSize: 12,
    color: ui.text,
    borderBottom: `1px solid ${ui.border}`,
    verticalAlign: 'top',
  };

  return (
    <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 780 }}>
      <thead>
        <tr>
          <th style={th}>Rendición</th>
          <th style={th}>Tipo</th>
          <th style={th}>Solicitante</th>
          <th style={{ ...th, textAlign: 'right' }}>Total</th>
          <th style={{ ...th, textAlign: 'right' }}>A devolver</th>
          <th style={{ ...th, textAlign: 'center' }}>Estado</th>
          <th style={{ ...th, textAlign: 'right' }} />
        </tr>
      </thead>
      <tbody>
        {isLoading ? (
          <tr>
            <td colSpan={7} style={{ ...td, textAlign: 'center', color: ui.muted }}>
              Cargando…
            </td>
          </tr>
        ) : error ? (
          <tr>
            <td colSpan={7} style={{ ...td, textAlign: 'center', color: '#dc2626' }}>
              {error}
            </td>
          </tr>
        ) : rows.length === 0 ? (
          <tr>
            <td colSpan={7} style={{ ...td, textAlign: 'center', color: ui.muted }}>
              {showApprovals
                ? 'No hay rendiciones pendientes de aprobación.'
                : 'No hay rendiciones en este período.'}
            </td>
          </tr>
        ) : (
          rows.map((row) => {
            const busy = actionBusyId === row.id;
            const canEdit = row.status === 'Pendiente' || row.status === 'Rechazada';
            return (
              <tr key={row.id}>
                <td style={td}>
                  <div style={{ fontWeight: 700 }}>{row.report_code}</div>
                  <div style={{ fontSize: 11, color: ui.muted, marginTop: 2 }}>
                    {String(row.report_date || '').slice(0, 10)}
                    {row.items_count ? ` · ${row.items_count} ítem${row.items_count === 1 ? '' : 's'}` : ''}
                  </div>
                  {row.description ? (
                    <div style={{ fontSize: 11, color: ui.muted, marginTop: 2 }}>{row.description}</div>
                  ) : null}
                </td>
                <td style={td}>{row.report_type || '—'}</td>
                <td style={td}>
                  <div style={{ fontWeight: 600 }}>{row.requester_name || '—'}</div>
                  {row.requester_rut ? (
                    <div style={{ fontSize: 11, color: ui.muted, marginTop: 2 }}>{row.requester_rut}</div>
                  ) : null}
                </td>
                <td style={{ ...td, textAlign: 'right', fontWeight: 700 }}>{money(row.total_amount)}</td>
                <td style={{ ...td, textAlign: 'right', fontWeight: 600 }}>
                  {money(row.total_to_return != null ? row.total_to_return : row.total_amount)}
                </td>
                <td style={{ ...td, textAlign: 'center' }}>
                  <StatusChip status={row.status} isDarkMode={isDarkMode} />
                  {row.rejection_reason ? (
                    <div style={{ fontSize: 11, color: ui.muted, marginTop: 4 }}>{row.rejection_reason}</div>
                  ) : null}
                </td>
                <td style={{ ...td, textAlign: 'right' }}>
                  <div style={{ display: 'inline-flex', gap: 6, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                    {showApprovals ? (
                      <>
                        <button
                          type="button"
                          disabled={busy}
                          onClick={() => onApprove(row)}
                          style={{
                            ...ghostBtnBase(ui),
                            borderColor: isDarkMode ? '#22c55e' : '#86efac',
                            color: isDarkMode ? '#86efac' : '#15803d',
                          }}
                          title="Aprobar"
                        >
                          <Check size={14} /> Aprobar
                        </button>
                        <button
                          type="button"
                          disabled={busy}
                          onClick={() => onReject(row)}
                          style={{
                            ...ghostBtnBase(ui),
                            borderColor: isDarkMode ? '#f87171' : '#fecaca',
                            color: isDarkMode ? '#fca5a5' : '#b91c1c',
                          }}
                          title="Rechazar"
                        >
                          <X size={14} /> Rechazar
                        </button>
                      </>
                    ) : null}
                    {canEdit ? (
                      <button type="button" disabled={busy} onClick={() => onEdit(row)} style={ghostBtnBase(ui)} title="Editar">
                        <Pencil size={14} />
                      </button>
                    ) : null}
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => onDownloadPdf?.(row)}
                      style={ghostBtnBase(ui)}
                      title="Descargar PDF"
                    >
                      <FileDown size={14} /> PDF
                    </button>
                    <button type="button" disabled={busy} onClick={() => onDelete(row)} style={ghostBtnBase(ui)} title="Anular">
                      <Trash2 size={14} />
                    </button>
                  </div>
                </td>
              </tr>
            );
          })
        )}
      </tbody>
    </table>
  );
}

function ItemsTable({ rows, isLoading, error, isDarkMode, ui }) {
  const th = {
    textAlign: 'left',
    padding: '8px 10px',
    fontSize: 11,
    fontWeight: 700,
    color: ui.muted,
    borderBottom: `1px solid ${ui.border}`,
    whiteSpace: 'nowrap',
  };
  const td = {
    padding: '9px 10px',
    fontSize: 12,
    color: ui.text,
    borderBottom: `1px solid ${ui.border}`,
    verticalAlign: 'top',
  };

  return (
    <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 820 }}>
      <thead>
        <tr>
          <th style={th}>Fecha</th>
          <th style={th}>Rendición</th>
          <th style={th}>Categoría</th>
          <th style={th}>Descripción</th>
          <th style={th}>Pago / Titular</th>
          <th style={th}>Emisor</th>
          <th style={th}>Solicitante</th>
          <th style={{ ...th, textAlign: 'right' }}>Monto</th>
          <th style={{ ...th, textAlign: 'center' }}>Estado</th>
        </tr>
      </thead>
      <tbody>
        {isLoading ? (
          <tr>
            <td colSpan={9} style={{ ...td, textAlign: 'center', color: ui.muted }}>
              Cargando…
            </td>
          </tr>
        ) : error ? (
          <tr>
            <td colSpan={9} style={{ ...td, textAlign: 'center', color: '#dc2626' }}>
              {error}
            </td>
          </tr>
        ) : rows.length === 0 ? (
          <tr>
            <td colSpan={9} style={{ ...td, textAlign: 'center', color: ui.muted }}>
              No hay ítems de rendición en este período.
            </td>
          </tr>
        ) : (
          rows.map((row) => (
            <tr key={row.id}>
              <td style={td}>{String(row.item_date || row.report_date || '').slice(0, 10) || '—'}</td>
              <td style={td}>
                <div style={{ fontWeight: 700 }}>{row.report_code}</div>
                <div style={{ fontSize: 11, color: ui.muted }}>{row.report_type || ''}</div>
              </td>
              <td style={td}>{row.category || '—'}</td>
              <td style={td}>
                <div>{row.description || '—'}</div>
                {row.document_number ? (
                  <div style={{ fontSize: 11, color: ui.muted, marginTop: 2 }}>Doc. {row.document_number}</div>
                ) : null}
              </td>
              <td style={td}>
                <div style={{ fontWeight: 600 }}>{row.payment_method || '—'}</div>
                <div style={{ fontSize: 11, color: ui.muted, marginTop: 2 }}>
                  {row.titular || 'Personal'}
                </div>
              </td>
              <td style={td}>
                <div style={{ fontWeight: 600 }}>{row.issuer_name || '—'}</div>
                {row.issuer_rut ? (
                  <div style={{ fontSize: 11, color: ui.muted, marginTop: 2 }}>{row.issuer_rut}</div>
                ) : null}
              </td>
              <td style={td}>{row.requester_name || '—'}</td>
              <td style={{ ...td, textAlign: 'right', fontWeight: 700 }}>
                <div>
                  {Number(row.amount || 0).toLocaleString('es-CL', {
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 2,
                  })}{' '}
                  {row.currency || 'CLP'}
                </div>
                {String(row.currency || 'CLP').toUpperCase() !== 'CLP' ? (
                  <div style={{ fontSize: 11, color: ui.muted, marginTop: 2, fontWeight: 500 }}>
                    × {Number(row.exchange_rate || 1).toLocaleString('es-CL', { maximumFractionDigits: 4 })} →{' '}
                    {money(Number(row.amount || 0) * Number(row.exchange_rate || 1))}
                  </div>
                ) : null}
              </td>
              <td style={{ ...td, textAlign: 'center' }}>
                <StatusChip status={row.report_status} isDarkMode={isDarkMode} />
              </td>
            </tr>
          ))
        )}
      </tbody>
    </table>
  );
}

function ghostBtnBase(ui) {
  return {
    height: 28,
    borderRadius: 8,
    border: `1px solid ${ui.border}`,
    background: 'transparent',
    color: ui.text,
    padding: '0 8px',
    cursor: 'pointer',
    fontSize: 12,
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
  };
}

export default AccountingRendicionesPanel;
